import React from 'react';
import { motion } from 'framer-motion';
import { useStory } from '../context/StoryContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const StatCard = ({ icon, label, value, sub, color }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-white dark:bg-purple-900/40 rounded-3xl border border-gray-100 dark:border-purple-800 p-6 flex items-start space-x-4 shadow-sm">
    <div className={`p-3 rounded-2xl ${color}`}>
      <SafeIcon icon={icon} className="text-xl text-white" />
    </div>
    <div>
      <p className="text-2xl font-black text-gray-900 dark:text-white">{value}</p>
      <p className="text-xs font-bold text-gray-900 dark:text-purple-400 uppercase tracking-widest">{label}</p>
      {sub && <p className="text-[10px] text-gray-900 dark:text-purple-400 mt-0.5">{sub}</p>}
    </div>
  </motion.div>
);

const MiniBar = ({ label, value, max, color }) => (
  <div className="space-y-1.5">
    <div className="flex justify-between items-center">
      <span className="text-[10px] font-bold text-gray-900 dark:text-purple-400 uppercase tracking-wide">{label}</span>
      <span className="text-[10px] font-black text-gray-900 dark:text-white">{value}</span>
    </div>
    <div className="h-2 bg-gray-100 dark:bg-purple-800 rounded-full overflow-hidden">
      <motion.div
        initial={{ width: 0 }} animate={{ width: `${Math.min(100, (value / max) * 100)}%` }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className={`h-full rounded-full ${color}`}
      />
    </div>
  </div>
);

const ActivityHeatmap = ({ log }) => {
  const last14 = Array.from({ length: 14 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (13 - i));
    const key = d.toISOString().split('T')[0];
    const entry = log.find(l => l.date === key);
    return { date: key, words: entry?.words || 0, label: d.toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric' }) };
  });
  const maxWords = Math.max(...last14.map(d => d.words), 1);

  return (
    <div className="space-y-3">
      <h4 className="text-[10px] font-black text-gray-900 dark:text-purple-400 uppercase tracking-widest">14-Day Writing Activity</h4>
      <div className="flex items-end space-x-1.5 h-20">
        {last14.map((day, i) => (
          <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative">
            <div className="absolute bottom-full mb-2 hidden group-hover:block bg-purple-900 text-white text-[9px] px-2 py-1 rounded-lg whitespace-nowrap z-10">
              {day.label}: {day.words} words
            </div>
            <motion.div
              initial={{ height: 0 }} animate={{ height: `${(day.words / maxWords) * 100}%` }}
              transition={{ delay: i * 0.03, duration: 0.5 }}
              className={`w-full rounded-t-md ${day.words > 0 ? 'bg-amber-500 hover:bg-amber-400' : 'bg-gray-100 dark:bg-purple-800'} transition-colors min-h-[4px]`}
            />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[8px] text-gray-900 dark:text-purple-400 font-bold">
        <span>14 days ago</span><span>Today</span>
      </div>
    </div>
  );
};

const Analytics = () => {
  const { state } = useStory();
  const { analytics, quests, archive, world, ai, character } = state;

  const completedQuests = quests.filter(q => q.status === 'Completed').length;
  const totalQuests = quests.length;
  const avgWordsPerSession = analytics?.sessionsCount > 0 ? Math.round(analytics.totalWords / analytics.sessionsCount) : (analytics?.totalWords || 0);
  const stats = character?.stats || { level: 1, health: 100, mana: 50, strength: 10, agility: 10, intelligence: 10, experience: 0 };

  return (
    <div className="max-w-5xl mx-auto px-6 py-12 space-y-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <p className="text-amber-500 text-sm font-bold tracking-widest uppercase mb-2">Insights</p>
        <h1 className="text-4xl font-serif font-bold text-gray-900 dark:text-white">Story Analytics</h1>
        <p className="text-gray-900 dark:text-purple-400 mt-2">Track your narrative progress and writing patterns.</p>
      </motion.div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FiIcons.FiFileText} label="Total Words" value={(analytics?.totalWords || 0).toLocaleString()} sub="across all sessions" color="bg-purple-600" />
        <StatCard icon={FiIcons.FiBook} label="Chapters" value={archive?.length || 0} sub="archived stories" color="bg-amber-600" />
        <StatCard icon={FiIcons.FiTarget} label="Quests Done" value={completedQuests} sub={`of ${totalQuests} total`} color="bg-emerald-600" />
        <StatCard icon={FiIcons.FiMessageSquare} label="Story Entries" value={analytics?.totalEntries || 0} sub="narrative beats" color="bg-blue-600" />
      </div>

      {/* Activity Heatmap */}
      <div className="bg-white dark:bg-purple-900/40 rounded-3xl border border-gray-100 dark:border-purple-800 p-8 shadow-sm">
        <ActivityHeatmap log={analytics?.activityLog || []} />
      </div>

      {/* Two-column: Character RPG Stats + Progress */}
      {state.narrativeStyle === 'adventurer' && (
        <div className="bg-white dark:bg-purple-900/40 rounded-3xl border border-gray-100 dark:border-purple-800 p-8 shadow-sm space-y-6">
          <h4 className="text-[10px] font-black text-gray-900 dark:text-purple-400 uppercase tracking-widest">{character.name || 'Character'} - Level {stats.level}</h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
               <MiniBar label="Health (HP)" value={stats.health} max={stats.health + 50} color="bg-red-500" />
               <MiniBar label="Mana (MP)" value={stats.mana} max={stats.mana + 50} color="bg-blue-500" />
               <MiniBar label="Experience (XP)" value={stats.experience} max={100} color="bg-amber-400" />
            </div>
            <div className="space-y-4">
               <MiniBar label="Strength (STR)" value={stats.strength} max={30} color="bg-orange-500" />
               <MiniBar label="Agility (AGI)" value={stats.agility} max={30} color="bg-green-500" />
               <MiniBar label="Intelligence (INT)" value={stats.intelligence} max={30} color="bg-purple-500" />
            </div>
          </div>
        </div>
      )}

      {/* Two-column: Progress + Genre */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-purple-900/40 rounded-3xl border border-gray-100 dark:border-purple-800 p-8 shadow-sm space-y-6">
          <h4 className="text-[10px] font-black text-gray-900 dark:text-purple-400 uppercase tracking-widest">Story Progress</h4>
          <MiniBar label="World Progress" value={world.progress || 5} max={100} color="bg-gray-500" />
          <MiniBar label="Quest Completion" value={completedQuests} max={Math.max(totalQuests, 1)} color="bg-amber-500" />
          <MiniBar label="Chapters Archived" value={archive?.length || 0} max={Math.max((archive?.length || 0) + 3, 5)} color="bg-emerald-500" />
          <MiniBar label="Story Entries" value={analytics?.totalEntries || 0} max={Math.max((analytics?.totalEntries || 0) + 20, 50)} color="bg-blue-500" />
        </div>

        <div className="bg-white dark:bg-purple-900/40 rounded-3xl border border-gray-100 dark:border-purple-800 p-8 shadow-sm space-y-6">
          <h4 className="text-[10px] font-black text-gray-900 dark:text-purple-400 uppercase tracking-widest">Session Info</h4>
          <div className="space-y-4">
            {[
              { label: 'Current Genre', value: world.genre },
              { label: 'Narrative POV', value: world.pov },
              { label: 'AI Model', value: ai.model },
              { label: 'Narrative Style', value: state.narrativeStyle === 'writer' ? 'Writer Mode' : 'Adventurer Mode' },
              { label: 'Creativity Setting', value: `${ai.settings?.creativity} / 1.0` },
              { label: 'Verbosity', value: ai.settings?.verbosity },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between items-center py-2 border-b border-purple-50 dark:border-purple-800/60 last:border-0">
                <span className="text-[10px] font-bold text-gray-900 dark:text-purple-400 uppercase tracking-wide">{label}</span>
                <span className="text-xs font-bold text-gray-900 dark:text-white">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Archive Summary */}
      {archive.length > 0 && (
        <div className="bg-white dark:bg-purple-900/40 rounded-3xl border border-gray-100 dark:border-purple-800 p-8 shadow-sm">
          <h4 className="text-[10px] font-black text-gray-900 dark:text-purple-400 uppercase tracking-widest mb-6">Chapter Archive Overview</h4>
          <div className="space-y-3">
            {archive.map((ch, i) => (
              <div key={ch.id} className="flex items-center justify-between py-3 border-b border-purple-50 dark:border-purple-800/60 last:border-0">
                <div className="flex items-center space-x-3">
                  <span className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[10px] font-black flex items-center justify-center">{i + 1}</span>
                  <div>
                    <p className="text-xs font-bold text-gray-900 dark:text-white">{ch.title}</p>
                    {ch.summary && <p className="text-[9px] text-gray-900 dark:text-purple-400 mt-0.5 line-clamp-1">{ch.summary}</p>}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs font-black text-amber-600">{ch.wordCount || ch.content.length * 15} words</p>
                  <p className="text-[9px] text-gray-900 dark:text-purple-400">{new Date(ch.timestamp).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;